import { useEffect } from 'react';
import { ExternalLink, Info, X } from 'lucide-react';
import { SocialPlatform } from '@mg/shared';
import { cn } from '../lib/cn';
import { FacebookIcon } from '../features/social/brand-icons';
import { PLATFORM_META } from '../features/social/platforms';

/**
 * Standalone, self-contained "Create a Facebook Page" mini-guide, opened in a new
 * tab from the connect flow. Renders OUTSIDE the agency nav shell and requires no
 * auth, so it reads like a clean help doc.
 */
export function FacebookPageGuide() {
  useEffect(() => {
    const prev = document.title;
    document.title = 'Create a Facebook Page · Smorgas Social';
    return () => {
      document.title = prev;
    };
  }, []);

  const steps: { title: string; body: React.ReactNode }[] = [
    {
      title: 'Go to Facebook and start a new Page',
      body: (
        <>
          Open{' '}
          <a
            href="https://www.facebook.com/pages/create"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 font-medium text-link hover:text-action-hover"
          >
            facebook.com/pages/create <ExternalLink className="h-3.5 w-3.5" aria-hidden />
          </a>{' '}
          and log into your Facebook account.
        </>
      ),
    },
    {
      title: 'Name your Page',
      body: (
        <>
          Choose <span className="font-semibold">&ldquo;Business or brand&rdquo;</span>, enter your{' '}
          <span className="font-semibold">Page name</span> and a{' '}
          <span className="font-semibold">category</span> that fits your business, then click{' '}
          <span className="font-semibold">Create Page</span>.
        </>
      ),
    },
    {
      title: 'Add the finishing touches (optional)',
      body: <>Add a profile photo and cover image, plus a short description of what you do.</>,
    },
    {
      title: 'Come back and connect',
      body: (
        <>
          Your Page is live! Return to Smorgas Social, click{' '}
          <span className="font-semibold">Connect Facebook</span>, and choose this Page from the
          list.
        </>
      ),
    },
  ];

  return (
    // h-full + overflow-y-auto: these guides are STANDALONE routes (no AppShell), and
    // body/#root are locked to overflow:hidden so the app's inner regions own scrolling.
    // Without its own scroll region a guide taller than the viewport is simply clipped.
    <div className="h-full overflow-y-auto bg-page text-ink">
      <div className="mx-auto max-w-2xl px-5 py-10 sm:py-14">
        {/* Branding. The page follows the theme, so the logo does too: dark lettering on the light
            page, white on the dark one. The files have no size of their own, hence width and height,
            drawn in rem by the classes so the logo scales with the page (index.css). */}
        <div className="mb-8 flex items-center">
          <img
            src="/smorgas-social-logo-light.svg"
            alt="Smorgas Social"
            width={137}
            height={21}
            className="block h-[1.3125rem] w-[8.5625rem] dark:hidden"
          />
          <img
            src="/smorgas-social-logo.svg"
            alt="Smorgas Social"
            width={137}
            height={21}
            className="hidden h-[1.3125rem] w-[8.5625rem] dark:block"
          />
        </div>

        <div className="rounded-2xl border border-line bg-card p-6 shadow-card sm:p-8">
          <div className="mb-6 flex items-start gap-3">
            <span
              className={cn(
                'flex h-12 w-12 shrink-0 items-center justify-center rounded-xl',
                PLATFORM_META[SocialPlatform.FACEBOOK].tile,
              )}
            >
              <FacebookIcon className="h-6 w-6" />
            </span>
            <div>
              <h1 className="text-xl font-bold tracking-tight text-ink">
                How to create a Facebook Page
              </h1>
              <p className="mt-1 text-sm text-ink-muted">
                A Facebook <span className="font-medium">Business Page</span> is what you connect to
                Smorgas Social. It only takes a couple of minutes.
              </p>
            </div>
          </div>

          <ol className="space-y-5">
            {steps.map((step, i) => (
              <li key={i} className="flex gap-4">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-tint text-sm font-bold text-tint-fg">
                  {i + 1}
                </span>
                <div className="pt-0.5">
                  <p className="text-sm font-semibold text-ink">{step.title}</p>
                  <p className="mt-1 text-sm leading-relaxed text-ink-label">{step.body}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="mt-7 flex gap-3 rounded-xl border border-line bg-tint/70 px-4 py-3">
            <Info className="mt-0.5 h-5 w-5 shrink-0 text-tint-fg" aria-hidden />
            <div className="text-sm text-ink-strong/90">
              <p className="font-semibold text-ink">Good to know</p>
              <ul className="mt-1 list-disc space-y-1 pl-4">
                <li>
                  You must be an <span className="font-semibold">admin</span> of the Page to connect
                  it.
                </li>
                <li>
                  Only <span className="font-semibold">Pages</span> can be connected — not personal
                  Facebook profiles.
                </li>
              </ul>
            </div>
          </div>
        </div>

        <p className="mt-6 flex items-center justify-center gap-1.5 text-center text-sm text-ink-faint">
          <X className="h-4 w-4" aria-hidden /> You can close this tab and return to Smorgas Social
          when you&apos;re done.
        </p>
      </div>
    </div>
  );
}
